import React from 'react';
import { Table, Card, Typography, Spin, Alert, Input, Popconfirm, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { VwProjSubsystems } from '../../../types';
import { UseGetVwProjSubsystems } from '../../../hooks/genericHooks';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { useTablePagination } from '../../../components/common/useTablePagination';
import StandardTable from '../../../components/common/StandardTable';
import Button from '../../../components/common/Button';

const { Title } = Typography;

const ToPlnProjSubSystemsList: React.FC = () => {
  const { data, isLoading, error, refetch } = UseGetVwProjSubsystems();
  const [search, setSearch] = React.useState('');
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const navigate = useNavigate();

  // Filtered data for search
  const filteredData = React.useMemo(() => {
    if (!data?.data) return [];
    return data.data.filter(
      (item) =>
        (typeof item.system_id === 'string' && item.system_id.toLowerCase().includes(search.toLowerCase())) ||
        (typeof item.sys_name === 'string' && item.sys_name.toLowerCase().includes(search.toLowerCase())) ||
        (typeof item.subsystem_name === 'string' && item.subsystem_name.toLowerCase().includes(search.toLowerCase()))
    );
  }, [data, search]);

  const { getPaginationProps } = useTablePagination(20);

  const handleDelete = async (record: any) => {
    setDeleteLoading(true);
    try {
      // await deleteData('vw_proj_subsystems', 'id', record.id);
      message.success('Subsystem deleted (mock)!');
      refetch && refetch();
    } catch (err: any) {
      message.error('Failed to delete: ' + (err?.message || 'Unknown error'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns: any[] = [
    {
      title: 'SysID',
      dataIndex: 'system_id',
      key: 'system_id',
      width: 40,
      ellipsis: false,
      sorter: (a: VwProjSubsystems, b: VwProjSubsystems) => String(a.system_id).localeCompare(String(b.system_id)),
      render: (system_id: string, record: VwProjSubsystems) => (
        <span
          title={record.sys_name || ''}
          style={{
            display: 'inline-block',
            maxWidth: 40,
            minWidth: 0,
            //overflow: 'hidden',
            //textOverflow: 'ellipsis',
            //whiteSpace: 'nowrap',
            padding: '0 0px',
            fontSize: 12,
            verticalAlign: 'middle',
          }}
        >
          {system_id}
        </span>
      ),
      exportValue: (r: VwProjSubsystems) => String(r.system_id ?? ''),
    },
    {
      title: 'SubsysID',
      dataIndex: 'subsystem_id',
      key: 'subsystem_id',
      width: 75,
      ellipsis: false,
      sorter: (a: VwProjSubsystems, b: VwProjSubsystems) => String(a.subsystem_id).localeCompare(String(b.subsystem_id)),
      render: (subsystem_id: string) => (
        <span
          style={{
            display: 'inline-block',
            maxWidth: 75,
            minWidth: 0,
            //overflow: 'hidden',
            //textOverflow: 'ellipsis',
            //whiteSpace: 'nowrap',
            padding: '0 0px',
            fontSize: 12,
            verticalAlign: 'middle',
          }}
        >
          {subsystem_id}
        </span>
      ),
      exportValue: (r: VwProjSubsystems) => String(r.subsystem_id ?? ''),
    },
    {
      title: 'Subsystem Name',
      dataIndex: 'subsystem_name',
      key: 'subsystem_name',
      sorter: (a: VwProjSubsystems, b: VwProjSubsystems) => String(a.subsystem_name).localeCompare(String(b.subsystem_name)),
      width: 300,
      exportValue: (r: VwProjSubsystems) => r.subsystem_name ?? '',
    },
    {
      title: 'IMC',
      dataIndex: 'imc_group',
      key: 'imc_group',
      width: 30,
      render: (val: number | null) => val ?? '-',
      exportValue: (r: VwProjSubsystems) => (r.imc_group ?? ''),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 20,
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
        { text: 'Out of Service', value: 'out_of_service' },
      ],
      onFilter: (value: any, record: VwProjSubsystems) => record.status === value,
      render: (status: string) => {
        let color = 'default';
        if (status === 'active') color = 'green';
        else if (status === 'inactive') color = 'orange';
        else if (status === 'out_of_service') color = 'red';
        return <span style={{ color }}>{status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>;
      },
      exportValue: (r: VwProjSubsystems) => (r.status ? r.status.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) : ''),
    },
    {
      title: 'Requirement',
      dataIndex: 'requirement',
      key: 'requirement',
      width: 90,
      render: (val: string | null) => val || '-',
      exportValue: (r: VwProjSubsystems) => r.requirement ?? '',
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 80,
      render: (val: string | null) => val || '-',
      exportValue: (r: VwProjSubsystems) => r.remarks ?? '',
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center' as const,
      render: (_: any, record: VwProjSubsystems) => (
        <span>
          <Button
            action="edit"
            icon={<EditOutlined />}
            size="small"
            type="primary"
            style={{ marginRight: 8 }}
            onClick={() => navigate(`/to/planning/proj_subsystems/edit/${record.id}`)}
          />
          <Popconfirm
            title="Are you sure delete this subsystem?"
            onConfirm={() => handleDelete(record)}
            okButtonProps={{ loading: deleteLoading }}
          >
            <Button action="delete" icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </span>
      ),
      width: 80,
    },
  ];

  return (
    <Card style={{ margin: 16, padding: 0 }} styles={{ body: { padding: 0 } }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px 8px 24px', borderBottom: '1px solid #f0f0f0' }}>
        <Title level={4} style={{ margin: 0 }}>Project Subsystems</Title>
        <Button
          action="add"
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/to/planning/proj_subsystems/create')}
        >
          Add Subsystem
        </Button>
      </div>
      <Input.Search
        placeholder="Search by System ID, System Name, or Subsystem Name"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ width: 350, margin: 8 }}
      />
      {error && <Alert type="error" message={error.message || 'Error loading data'} showIcon />}
      <Spin spinning={isLoading} tip="Loading...">
        <StandardTable
          dataSource={filteredData}
          columns={columns}
          rowKey="id"
          pagination={getPaginationProps(filteredData.length)}
          bordered
          size="small"
          scroll={{ x: 'max-content' }}
          enableExport={true}
        />
      </Spin>
    </Card>
  );
};

export default ToPlnProjSubSystemsList;
